import { useAutoneTranslation } from '@autone/translations';
import { TailwindAlert } from '@autone/ui';
import { fNumber } from '@autone/utils';
import { TriangleAlert } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../../consts';
import { type AggregationType } from '../../aggregation-type-radio-toggle-group';

interface ExportLimitExceededBannerProps {
  aggregationType: AggregationType;
  exportRowCount: number;
  exportRowCountUpperLimit: number;
  exportRowCountLowerLimit: number;
}

export const ExportLimitExceededBanner = ({
  aggregationType,
  exportRowCount,
  exportRowCountUpperLimit,
  exportRowCountLowerLimit,
}: ExportLimitExceededBannerProps) => {
  const { t } = useAutoneTranslation<typeof APP_NAME>();
  const aggregationTypeText = useMemo(() => {
    switch (aggregationType) {
      case 'product_location':
        return t('export-limit-banner.aggregation-type.product');
      case 'sku_location':
        return t('export-limit-banner.aggregation-type.sku');
    }
  }, [aggregationType, t]);

  const exceedsUpperLimit = exportRowCount > exportRowCountUpperLimit;

  return (
    <motion.div
      className="sticky bottom-6 mt-6 px-6 z-40 bg-transparent"
      initial={{ translateY: 100 }}
      animate={{ translateY: 0 }}
      exit={{ translateY: 100 }}
      transition={{
        type: 'spring',
        stiffness: 350,
        damping: 25,
      }}
    >
      <TailwindAlert variant={'error'} className="shadow-md">
        <div className="flex flex-row gap-3 items-center w-full">
          <TriangleAlert size={30} />
          <p className="font-medium">
            {t(
              `export-limit-banner.${exceedsUpperLimit ? 'upper-limit-exceeded-banner-text' : 'lower-limit-exceeded-banner-text'}`,
              {
                aggregationType: aggregationTypeText,
                rowCount: fNumber(exportRowCount),
                rowLimit: fNumber(
                  exceedsUpperLimit
                    ? exportRowCountUpperLimit
                    : exportRowCountLowerLimit,
                ),
              },
            )}
          </p>
        </div>
      </TailwindAlert>
    </motion.div>
  );
};
